/**
 * CheckoutStepReview Component
 */

import * as cartActions from '../../../redux/actions/cart';
import * as checkoutActions from '../../../redux/actions/checkout';
import { getCountryLabelFromCode, getStateLabelFromCode } from '../../../redux/helpers/countries';
import { generateLicenseUsageFromComponents, usageToString } from '../../../redux/helpers/license';
import { formatAddress } from '../../../utils/address';
import { nl2br } from '../../../utils/react';
import { bw5, bw6 } from '../../styles/colors';

import { find, isEqual, size } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import { Field, FormProps, getFormValues, reduxForm } from 'redux-form';
import * as Velocity from 'velocity-animate';

import Button from '../../atoms/buttons/Button';
import LicenseBuilderButton from '../../atoms/buttons/LicenseBuilderButton';
import Checkbox from '../../atoms/forms/Checkbox';
import { IconEdit } from '../../atoms/images/Icon';
import Heading from '../../atoms/text/Heading';
import { Span } from '../../atoms/text/Label';
import PriceTag from '../../atoms/text/PriceTag';
import FormCell from '../../molecules/forms/FormCell';
import FormRow from '../../molecules/forms/FormRow';
import CheckoutAmounts from './CheckoutAmounts';
import CheckoutStep from './CheckoutStep';
import { CheckoutStepBillingFormFields } from './CheckoutStepBilling';
import { CheckoutStepLicenseFormFields } from './CheckoutStepLicense';
import { CheckoutStepProjectFormFields } from './CheckoutStepProject';
import { CheckoutStepFormName, CheckoutStepId, kCheckoutNoInputLabel } from './config';

const formValidations: IFormValidations = {
  checkTerms: {
    error: 'You must agree to both terms and obligations.',
  },
  obligationsTerms: {
    error: 'You must agree to both terms and obligations.',
  },
};

interface IFormFields {
  checkTerms: boolean;
  obligationsTerms: boolean;
}

interface IOwnStateProps {
  billingInformation: CheckoutStepBillingFormFields;
  isActive: boolean;
  license: ITermsState;
  licenseInformation: CheckoutStepLicenseFormFields;
  projectDetails: CheckoutStepProjectFormFields;
  stripeToken: stripe.Token;
  getCountryLabel(countryCode: string): string | undefined;
  getStateLabel(stateCode: string): string | undefined;
}

interface IOwnDispatchProps {
  setActiveStep(id: string): void;
  updateCartItemTerms(lineItemId: string, terms: ILicenseUsage[]): Promise<any>;
}

interface IOwnProps {
  companyTypes: GQL.ICompanyTypeConnection;
  representativeRoles: GQL.IRepresentativeRoleConnection;
  order: GQL.IOrder;
}

type CheckoutStepReviewProps =
  IOwnProps
  & IOwnStateProps
  & IOwnDispatchProps
  & FormProps<any, any, {}>;

const stepId = CheckoutStepId.review;
const form = CheckoutStepFormName.review;
const classes = new BEM(`checkout__step--${stepId}`);

class CheckoutStepReview extends React.Component<CheckoutStepReviewProps, {}> {

  componentWillReceiveProps({ isActive, license }: CheckoutStepReviewProps) {
    if (license.scope && license.scope.type === 'CartLineItem') {
      if (isEqual(license.scope, this.props.license.scope)) {
        if (!isEqual(license.usages, this.props.license.usages)) {
          this.props.updateCartItemTerms(license.scope.id as string, license.usages);
        }
      }
    }
  }

  shouldComponentUpdate(nextProps: CheckoutStepReviewProps) {
    return nextProps.isActive;
  }

  componentDidUpdate(prevProps: CheckoutStepReviewProps) {
    if (!prevProps.isActive && this.props.isActive) {
      const container = document.getElementsByClassName('checkout__content-wrapper')[0];
      const scrollPosition = container.scrollTop;
      const offset = -scrollPosition + 24;
      Velocity(findDOMNode(this), 'scroll', { container, offset, delay: 500 });
    }
  }

  onSaveLicenseLineItem = (itemId: string) => (usages: ILicenseUsage[]) => {
    this.props.updateCartItemTerms(itemId, usages);
  }

  renderLicense = (order: GQL.IOrder, lineItem: GQL.ILicenseLineItem) => {
    const { projectDetails } = this.props;
    const track = lineItem.track!;
    const usages = lineItem.license!.usages!.map(
      ({ id, components }) => generateLicenseUsageFromComponents(components!, id!),
    );
    return (
      <div key={track.id} {...classes('license-item') }>
        <Heading is="h5" WrappedComponent="div">{track.title!}</Heading>
        <div>
          {usages.map((usage, i) => (
            <Heading
              is="h5"
              WrappedComponent="div"
              {...classes('license-item__usage') }
              key={`license-usage-${i}`}
            >
              {usageToString(usage)}
              {projectDetails && projectDetails.airDate
                ? (<Span textColor={bw6}>&nbsp;({projectDetails.airDate[usage.id!]})</Span>)
                : ''}
            </Heading>
          ))}
        </div>
        <PriceTag
          amount={lineItem.amount!}
          rate={order.currency_rate!}
          currency={order.currency_code! as GQL.Currency}
          size="medium"
        />
        <LicenseBuilderButton
          variant="icon"
          icon={<IconEdit />}
          textColor={bw5}
          scope={{
            type: 'CartLineItem',
            id: lineItem.id,
          }}
          onSaveLicense={this.onSaveLicenseLineItem(lineItem.id)}
          {...classes('license-item__button-edit') }
        />
      </div>
    );
  }

  renderInformation(label: string, value: string | number | undefined | JSX.Element[]) {
    return value && (
      <div {...classes('information__item') }>
        <Span size="medium" textColor={bw6} {...classes('information__item__title') }>{label}:</Span>
        <Heading is="h5" WrappedComponent="div">{value}</Heading>
      </div>
    );
  }

  renderAddress(address: IAddress) {
    const addressString = formatAddress(address, this.props.getCountryLabel, this.props.getStateLabel);
    return this.renderInformation('Address', nl2br(addressString));
  }

  renderBillingAddress() {
    const billing = this.props.billingInformation;
    return billing.sameAddressAsLicensee === false && billing.address ?
      this.renderAddress(billing.address)
      : this.renderAddress(this.props.licenseInformation.address);
  }

  renderRepresentativeInformation() {
    const rep = this.props.licenseInformation.representative;
    const role = find(
      this.props.representativeRoles.edges!.map(({ node }) => node!),
      { id: rep.role })!.name!;
    const str = `${rep.name}\n${role}\n${rep.email}${rep.phone ? `\n${rep.phone}` : ``}`;
    return this.renderInformation('Authorized Representative', nl2br(str));
  }

  renderCardExpiration() {
    const { stripeToken } = this.props;
    const expMonth = stripeToken.card!.exp_month;
    const expYear = stripeToken.card!.exp_year;
    return this.renderInformation('Card Expiration', `${expMonth < 10 ? `0${expMonth}` : expMonth} / ${expYear}`);
  }

  renderPaymentInformation() {
    const {
      billingInformation,
      licenseInformation,
      stripeToken,
    } = this.props;
    const paymentMethod = billingInformation.paymentMethod;

    const cardInfo = () => (
      <>
        {this.renderInformation('Card', `**** **** **** ${stripeToken.card!.last4}`)}
        {this.renderCardExpiration()}
      </>
    );

    const chequeInfo = (
      <>
        {this.renderInformation('Payment Method', 'Cheque')}
      </>
    );

    return (
      <>
        {this.renderInformation(`Cardholder's Name`,
          billingInformation ? billingInformation.nameOnCard : licenseInformation.representative.name)}
        {this.renderBillingAddress()}
        {paymentMethod === GQL.OrderPaymentMethod.Stripe ? cardInfo() : chequeInfo}
      </>
    );
  }

  render() {
    const {
      billingInformation,
      companyTypes,
      error,
      handleSubmit,
      licenseInformation,
      projectDetails = {} as any,
      order,
      setActiveStep,
      submitting,
    } = this.props;

    const lineItems = order.license_line_items!;

    return (
      <CheckoutStep
        formValidations={formValidations}
        error={error}
        handleSubmit={handleSubmit!}
        id={stepId}
        showNextStepButton={false}
        title="Review"
      >
        <Heading is="h6">License(s)</Heading>
        <div {...classes('licenses') }>
          {lineItems.map((item) => this.renderLicense(order, item))}
        </div>
        <div {...classes('information') }>
          {(
            <section {...classes('information__section', 'project') }>
              <header {...classes('information__section__header') }>
                <Heading is="h6">Project Details</Heading>
                <Button
                  onClick={() => setActiveStep(CheckoutStepId.project)}
                  variant="icon"
                  icon={<IconEdit />}
                  textColor={bw5}
                />
              </header>
              {this.renderInformation('Brand', projectDetails.brand || kCheckoutNoInputLabel)}
              {this.renderInformation('Project Title', projectDetails.product || kCheckoutNoInputLabel)}
              {this.renderInformation('Reference', projectDetails.reference)}
              {this.renderInformation('PO Number', projectDetails.poNumber)}
              {this.renderInformation('Notes', projectDetails.notes)}
            </section>
          )}
          {size(licenseInformation) && (
            <section {...classes('information__section', 'license') }>
              <header {...classes('information__section__header') }>
                <Heading is="h6">Licensee Information</Heading>
                <Button
                  onClick={() => setActiveStep(CheckoutStepId.license)}
                  variant="icon"
                  icon={<IconEdit />}
                  textColor={bw5}
                />
              </header>
              {this.renderInformation('Company Name', licenseInformation.address.company.name)}
              {this.renderInformation('Company Type', find(
                companyTypes.edges!.map(({ node }) => node!),
                { id: licenseInformation.address.company.type })!.name!,
              )}
              {this.renderAddress(this.props.licenseInformation.address)}
              {this.renderRepresentativeInformation()}
            </section>
          )}
          {size(licenseInformation) && size(billingInformation) && (
            <section {...classes('information__section', 'billing') }>
              <header {...classes('information__section__header') }>
                <Heading is="h6">Billing Information</Heading>
                <Button
                  onClick={() => setActiveStep(CheckoutStepId.billing)}
                  variant="icon"
                  icon={<IconEdit />}
                  textColor={bw5}
                />
              </header>
              {this.renderPaymentInformation()}
            </section>
          )}
        </div>
        <div {...classes('summary') }>
          <CheckoutAmounts context="review" order={order} />
          <div {...classes('agreements') }>
            <FormRow>
              <FormCell minimumWidth={8}>
                <label {...classes('agreements__item') }>
                  <Field
                    name="checkTerms"
                    component={Checkbox}
                    size="large"
                    colorScheme="white"
                  />
                  <Span {...classes('agreements__item__text') } variant="description" size="large">
                    I have the authority to enter this license agreement on behalf of the licensee.
                  </Span>
                </label>
              </FormCell>
              <FormCell spacer minimumWidth={1} />
              <FormCell minimumWidth={8}>
                <label {...classes('agreements__item') }>
                  <Field
                    name="obligationsTerms"
                    component={Checkbox}
                    size="large"
                    colorScheme="white"
                  />
                  <Span {...classes('agreements__item__text') } variant="description" size="large">
                    I have read the Terms and Conditions, and I specifically certify that I will not use or facilitate
                    the use of the music obtained on Bopper in conjunction with any audio or video material that could
                    be deemed overtly racist, sexist or discriminatory or promotes, or is funded by, a political
                    organization or campaign, or that promotes cigarettes or any firearms-related subjects, such as
                    firearms products, their use, or any related legislative agenda, in accordance with our
                    Terms and Conditions.
                  </Span>
                </label>
              </FormCell>
            </FormRow>
          </div>
          <Button
            {...classes('button-order') }
            label="Place Order"
            size="xlarge"
            colorScheme="accent"
            disabled={submitting}
            submitting={submitting}
            isSubmit
          />
        </div>
      </CheckoutStep>
    );
  }
}

const CheckoutStepReviewForm = reduxForm<IFormFields, CheckoutStepReviewProps, any>({
  form,
})(CheckoutStepReview);

const ConnectedCheckoutStepReview = connect<IOwnStateProps, IOwnDispatchProps, IOwnProps>(
  (state: IStoreState): IOwnStateProps => ({
    billingInformation: getFormValues(CheckoutStepFormName.billing)(state) as CheckoutStepBillingFormFields,
    getCountryLabel: (countryCode) => {
      return getCountryLabelFromCode(countryCode)(state);
    },
    getStateLabel: (stateCode) => {
      return getStateLabelFromCode(stateCode)(state);
    },
    isActive: state.checkout.activeStep === stepId,
    license: state.license,
    licenseInformation: getFormValues(CheckoutStepFormName.license)(state) as CheckoutStepLicenseFormFields,
    projectDetails: getFormValues(CheckoutStepFormName.project)(state) as CheckoutStepProjectFormFields,
    stripeToken: state.user.stripeToken!,
  }),
  (dispatch) => ({
    setActiveStep: (id) => dispatch(checkoutActions.setActiveStep({ id })),
    updateCartItemTerms: (lineItemId, terms) =>
      new Promise((resolve, reject) => {
        dispatch(cartActions.requestUpdateCartItemTerms({
          lineItemId,
          promise: { resolve, reject },
          terms,
        }));
      }),
  }),
)(CheckoutStepReviewForm);

export default ConnectedCheckoutStepReview;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/Checkout/CheckoutStepReview.tsx